import React, { useState, useEffect } from "react";
import { wsRequest } from "./wsClient.js";
import { useAuthedImageUrl } from "./useAuthedImageUrl.js";
import {
  ReplayGuessForm,
  ReplayStatsPanel,
  ReplayLastEntriesPanel,
  ReplaySpeedControls,
  ReplayHowItWorksCard,
  ReplayResult,
  ReplayAsItHappened,
  ReplayStatusCard,
  ReplayViewToggle,
  ReplayImageOverlay,
  ReplayTransportControls,
  ReplayProgress,
  ReplayMyStatsPanel,
  ReplayFreeEntryBanner,
  buildEventTimeline,
  countUsersEntered,
  useReplayClock,
  useReplayPlayback,
} from "./replayShared.jsx";

const API_BASE = ""; // same-origin — nginx proxies /api and /ws to the backend

// Competition Management -> Competition History -> ▶ Play.
//
// The same player the public Demo Comps replay link shows, in a modal
// and for one competition rather than a looping round of five. Every
// piece of it — the panels, the event timeline, the playback loop —
// comes from replayShared.jsx, so this is genuinely the same player and
// not a lookalike that will drift from it.
//
// Two things it does NOT inherit from the public replay, both
// deliberate: there's no between-competition gap screen (nothing to
// advance to), and no email-capture form (that exists to show a
// prospective partner the re-engagement mechanism, which is not what an
// admin reviewing their own competition needs to see).

// ONE MODAL, TWO CALLERS.
//
// Written for an admin watching back a competition. A buyer watching one
// they played in wants exactly the same playback against different
// routes, so the two things that differ — where the data comes from and
// where the frames come from — are now props.
//
// Defaults to the admin behaviour when they are not given, so that
// caller is unchanged.
export default function CompetitionReplayModal({
  competitionId,
  token,
  onClose,
  loadData,
  progressiveImageUrl,
  revealedImageUrl,
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // 1 — the pace the competition actually ran at. See SPEEDS in
  // replayShared for what that does and does not mean.
  // 1 — the pace the competition actually ran at. See SPEEDS in
  // replayShared.jsx, and the test that pins every player and the
  // widget to the same starting speed: a replay that runs faster here
  // than in a partner's embed is not the same recording.
  const [speed, setSpeed] = useState(1);
  // FULL BY DEFAULT — a replay is meant to show the competition as it
  // ran, and the image on its own is the narrower view, not the normal
  // one. "Image only" is there for a small embed area where the picture
  // is the only part with room to be seen (20 Sep 2026).
  const [view, setView] = useState("full");

  useEffect(() => {
    let cancelled = false;
    (loadData ? loadData() : wsRequest("admin.competitions.replayData", { id: competitionId }, token))
      .then((body) => {
        if (!cancelled) setData(body);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Could not load this playback.");
      });
    return () => {
      cancelled = true;
    };
  }, [competitionId, token]);

  const comp = data?.competition;
  const events = comp ? buildEventTimeline(comp) : [];
  // OFF BY DEFAULT - see ReplayAsItHappened.
  const [asItHappened, setAsItHappened] = useState(false);
  const { phase, purchaseCount, guessCount, isLottery, playing, pause, resume, restart, speedRef, clockRef } = useReplayPlayback(
    comp,
    events,
    speed,
    asItHappened
  );
  // The competition's own clock, so the countdowns in the full view move
  // at the speed the replay is running.
  const replayNow = useReplayClock(comp, events, purchaseCount + guessCount, speed);

  // Escape closes, matching every other dismissable overlay in the
  // panel. Bound on the document rather than the modal itself so it
  // works without the modal having to hold focus.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Once revealed, the true final image comes from the already-public
  // route, exactly as the demo replay does — that route returns the
  // full revealed image for an ended competition on its own, so there's
  // nothing admin-gated about it and no blob fetch needed.
  // Null while there's nothing to fetch yet (no data, or already
  // revealed), which is how the shared hook is told to hold off.
  const { src: progressiveUrl, failed: imageFailed } = useAuthedImageUrl(
    comp && phase === "playing"
      ? progressiveImageUrl
        ? progressiveImageUrl(purchaseCount)
        : `${API_BASE}/api/admin/competitions/${competitionId}/replay-image?regionCount=${purchaseCount}`
      : null,
    token
  );
  // THE REVEALED FRAME NEEDS THE SAME TREATMENT when it comes from a
  // snapshot. The admin's default is the public image route, which works
  // because the competition still exists; a buyer's replay may outlive
  // the competition, so its final frame comes from the frozen copy — and
  // that route is authenticated, so it goes through the same blob fetch
  // rather than straight into an <img> src.
  const { src: revealedBlobUrl } = useAuthedImageUrl(
    revealedImageUrl && phase === "revealed" ? revealedImageUrl : null,
    token
  );
  const imageSrc =
    phase === "revealed"
      ? revealedImageUrl
        ? revealedBlobUrl
        : `${API_BASE}/api/competitions/${competitionId}/public-image?t=${competitionId}`
      : progressiveUrl;

  // WHAT WAS MINE, as at this point in the playback.
  //
  // `you` holds indexes into the arrays already on the page, worked out
  // by the server from ids it does not send. It is absent for a public
  // viewer, who is nobody in particular — which is the whole difference
  // between the two kinds of replay.
  const you = data?.you || null;
  const myRegionIndexes = (you?.regions || []).filter((i) => i < purchaseCount);
  const myGuessIndexes = (you?.guesses || []).filter((i) => i < guessCount);

  const playedRegions = comp ? comp.regions.slice(0, purchaseCount) : [];
  const playedGuesses = comp ? comp.guesses.slice(0, guessCount) : [];
  const usersEntered = countUsersEntered(playedRegions, playedGuesses);

  const backdrop = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "center",
    padding: 20,
    overflowY: "auto",
    zIndex: 1000,
  };
  const panel = {
    background: "var(--bg-panel-alt)",
    border: "1px solid var(--border-color)",
    borderRadius: 10,
    boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
    padding: 20,
    width: "100%",
    maxWidth: 900,
    color: "var(--text-primary)",
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12 }}>
          <div>
            <div style={{ fontSize: 12, opacity: 0.6 }}>Competition playback</div>
            {/* NOT "Loading…" WHEN IT HAS ALREADY FAILED. The error was
                shown underneath while the heading still said loading,
                which reads as a page stuck rather than a replay that
                is not available. Seen 18 Sep 2026 on a test
                competition opened in live mode — a correct refusal
                that looked like a hang. */}
            <h3 style={{ margin: "2px 0 0" }}>
              {comp?.title || (error ? "Replay unavailable" : "Loading…")}
            </h3>
          </div>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
            {comp && <ReplayViewToggle view={view} setView={setView} />}
            {comp && (
              <ReplayTransportControls
                playing={playing}
                phase={phase}
                onPause={pause}
                onResume={resume}
                onRestart={restart}
              />
            )}
            {comp && <ReplaySpeedControls speed={speed} setSpeed={setSpeed} />}
            {comp && <ReplayAsItHappened value={asItHappened} onChange={setAsItHappened} />}
            <button onClick={onClose}>Close</button>
          </div>
        </div>

        {error && <p style={{ opacity: 0.8 }}>{error}</p>}
        {!error && !comp && <p style={{ opacity: 0.7 }}>Loading…</p>}

        {comp && (
          <>
            {/* MOVED INTO replayShared AS ReplayResult (1.537.0), where
                it gained the winner, the drawn ticket and the prize -
                see its own comment on why our page was saying less
                about our competition than a partner's copy of it. */}
            {phase === "revealed" && <ReplayResult comp={comp} you={you} currencySymbol={data.currencySymbol} />}

            {/* THE CARDS THAT SAT ABOVE THE IMAGE on the live page,
                rebuilt from the recording. Only while playing: once the
                picture is revealed the competition is over, and a
                countdown to a finish that has happened is noise. */}
            {view === "full" && phase === "playing" && <ReplayFreeEntryBanner comp={comp} you={you} />}

            {view === "full" && phase === "playing" && (
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "stretch", marginBottom: 16, width: "100%" }}>
                <ReplayHowItWorksCard comp={comp} currencySymbol={data.currencySymbol} now={replayNow} />
                <ReplayStatusCard comp={comp} isLottery={isLottery} now={replayNow} />
              </div>
            )}

            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-start" }}>
              <div style={{ flex: "1 1 380px", minWidth: 260 }}>
                <div style={{ position: "relative", width: "100%", borderRadius: 8, overflow: "hidden", border: "1px solid var(--border-color)", minHeight: 120 }}>
                  {imageSrc ? (
                    <>
                      <img src={imageSrc} alt={comp.title} style={{ width: "100%", display: "block" }} />
                      {/* THE BOXES STAY WITH THE IMAGE, not with the full
                          view: somebody who switched to "Image only"
                          because the picture is small still wants to see
                          which part of it was theirs. */}
                      {phase === "playing" && (
                        <ReplayImageOverlay comp={comp} purchaseCount={purchaseCount} mineIndexes={myRegionIndexes} />
                      )}
                    </>
                  ) : (
                    <p style={{ fontSize: 13, opacity: 0.6, padding: 16, margin: 0 }}>
                      {imageFailed ? "This competition's image is no longer available." : "Loading image…"}
                    </p>
                  )}
                </div>

                {/* HOW FAR THROUGH, in both views: somebody watching the
                    image alone still wants to know where they are.

                    actualStartAt and answeredAt are what THIS payload
                    calls the two times; the widget's startedAt/endedAt
                    are its own mapping of the same pair. Written as
                    comp.startedAt first, which is undefined here - and
                    virtualElapsed answers null for that, so the clock
                    would simply never have appeared. */}
                <ReplayProgress
                  purchaseCount={purchaseCount}
                  totalPurchases={comp.regions.length}
                  guessCount={guessCount}
                  totalGuesses={comp.guesses.length}
                  playing={playing}
                  phase={phase}
                  clockRef={clockRef}
                  startedAt={comp.actualStartAt}
                  endedAt={comp.answeredAt}
                  speedRef={speedRef}
                />

                {view === "full" && phase === "playing" && (
                  <ReplayGuessForm
                    answerMode={comp.answerMode}
                    creditsPerEntry={comp.creditsPerEntry}
                    creditsPerMajorUnitOnPixels={data.creditsPerMajorUnitOnPixels}
                    currencySymbol={data.currencySymbol}
                    isLottery={isLottery}
                  />
                )}
              </div>

              {view === "full" && phase === "playing" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16, width: "100%", maxWidth: 260 }}>
                  <ReplayStatsPanel imagesRevealed={purchaseCount} usersEntered={usersEntered} entriesSubmitted={guessCount} />
                  {you && (
                    <ReplayMyStatsPanel
                      pixelsBought={myRegionIndexes.length}
                      entriesSubmitted={myGuessIndexes.length}
                      usedFreeEntry={you.usedFreeEntry}
                    />
                  )}
                  <ReplayLastEntriesPanel guesses={playedGuesses} />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
